import { ArrowRight, Clock, User, XCircle } from "lucide-react";
import React, { memo, useMemo } from "react";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { ClosureInfo, FloorplanBooking } from "../floorplanTypes";
import { formatTime, getStageColour, getStageInitial } from "../floorplanUtils";

type BadgePosition = "top" | "bottom" | "left" | "right";

interface FloatingBadgeProps {
  booking?: FloorplanBooking;
  nextBooking?: FloorplanBooking;
  tableName: string;
  tableWidth: number;
  tableHeight: number;
  tableX: number;
  tableY: number;
  position?: BadgePosition;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  isVisible?: boolean;
  showTableNameInBadge?: boolean;
  isDark?: boolean;
  isClosed?: boolean;
  closureInfo?: ClosureInfo;
  tooltipId?: string;
}

const CONNECTORS: Record<BadgePosition, string> = {
  bottom: "bottom-0 left-1/2 -translate-x-1/2 translate-y-full w-[1.5px] h-1",
  top: "top-0 left-1/2 -translate-x-1/2 -translate-y-full w-[1.5px] h-1",
  left: "left-0 top-1/2 -translate-y-1/2 -translate-x-full h-[1.5px] w-1",
  right: "right-0 top-1/2 -translate-y-1/2 translate-x-full h-[1.5px] w-1"
};

const FloatingBadgeComponent: React.FC<FloatingBadgeProps> = ({
  booking,
  nextBooking,
  tableName,
  tableWidth,
  tableHeight,
  tableX,
  tableY,
  position = "bottom",
  onMouseEnter,
  onMouseLeave,
  isVisible = true,
  showTableNameInBadge = false,
  isDark = false,
  isClosed = false,
  closureInfo,
  tooltipId
}) => {
  const { stages } = useDiaryContext();
  const hasBooking = !!booking;

  const stageColour = useMemo(
    () =>
      hasBooking
        ? getStageColour(stages, booking?.currentStage)
        : "transparent",
    [hasBooking, stages, booking?.currentStage]
  );

  const stageInitial = useMemo(
    () =>
      hasBooking
        ? (getStageInitial(booking?.currentStage) ?? (
            <Clock size={16} style={{ color: "black" }} />
          ))
        : "",
    [hasBooking, booking?.currentStage]
  );

  const closureTimeDisplay = useMemo((): string => {
    if (!closureInfo || closureInfo.isAllDay) return "All day";
    return `${closureInfo.timeFrom} - ${closureInfo.timeTo}`;
  }, [closureInfo]);

  const badgePosition = useMemo((): React.CSSProperties => {
    const centreX = tableX + tableWidth / 2;
    const gap = 2;

    switch (position) {
      case "bottom":
        return {
          left: `${centreX}px`,
          top: `${tableY + tableHeight + gap}px`,
          transform: "translate(-50%, 0)"
        };
      case "top":
        return {
          left: `${centreX}px`,
          top: `${tableY - gap}px`,
          transform: "translate(-50%, -100%)"
        };
      case "left":
        return {
          left: `${tableX - gap}px`,
          top: `${tableY + tableHeight / 2}px`,
          transform: "translate(-100%, -50%)"
        };
      case "right":
        return {
          left: `${tableX + tableWidth + gap}px`,
          top: `${tableY + tableHeight / 2}px`,
          transform: "translate(0, -50%)"
        };
    }
  }, [position, tableX, tableY, tableWidth, tableHeight]);

  const shouldShowTableName = useMemo(
    () => showTableNameInBadge || hasBooking || isClosed,
    [showTableNameInBadge, hasBooking, isClosed]
  );

  const bookingTimeRange = useMemo(
    () =>
      hasBooking
        ? `${formatTime(booking?.startTime ?? "")} - ${formatTime(booking?.endTime ?? "")}`
        : "",
    [hasBooking, booking?.startTime, booking?.endTime]
  );

  const nextBookingTimeRange = useMemo(
    () =>
      nextBooking
        ? `${formatTime(nextBooking.startTime)} - ${formatTime(nextBooking.endTime)}`
        : "",
    [nextBooking?.startTime, nextBooking?.endTime]
  );

  const themed = (dark: string, light: string): string =>
    isDark ? dark : light;

  const visibilityClass = isVisible
    ? "opacity-100 animate-in fade-in slide-in-from-bottom-1"
    : "opacity-0 pointer-events-none";

  const connectorBg = themed("bg-slate-700", "bg-slate-300");
  const cardBg = themed(
    "bg-slate-900 border-slate-700",
    "bg-white border-slate-200"
  );
  const tableNameColour = themed("text-slate-300", "text-slate-700");
  const closedTextColour = themed("text-red-400", "text-red-600");
  const closedTimeColour = themed("text-slate-300", "text-slate-600");
  const clockColour = themed("text-slate-400", "text-slate-500");
  const dividerColour = themed("bg-slate-700", "bg-slate-200");
  const timeColour = themed("text-white", "text-slate-900");
  const guestColour = themed("text-slate-200", "text-slate-700");
  const guestIconColour = themed("text-slate-300", "text-slate-600");

  return (
    <div
      data-floating-badge
      role="tooltip"
      id={tooltipId}
      className={`absolute transition-all duration-200 ${visibilityClass}`}
      style={{
        ...badgePosition,
        pointerEvents: isVisible ? "auto" : "none",
        zIndex: 99999
      }}
      onMouseEnter={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onMouseEnter?.();
      }}
      onMouseLeave={(e) => {
        e.stopPropagation();
        onMouseLeave?.();
      }}
    >
      <div className={`absolute ${CONNECTORS[position]} ${connectorBg}`} />

      <div
        className={`rounded-lg px-3 py-1.5 shadow-xl border ${cardBg}`}
        style={{ pointerEvents: "auto" }}
      >
        <div className="flex flex-col items-center gap-1">
          {shouldShowTableName && (
            <div className={`text-[11px] font-black ${tableNameColour}`}>
              {tableName}
            </div>
          )}

          {isClosed && (
            <>
              <div className="flex items-center gap-1.5">
                <XCircle
                  size={12}
                  className="text-red-500 flex-shrink-0"
                  aria-hidden="true"
                />
                <span
                  className={`text-[11px] font-black uppercase ${closedTextColour}`}
                >
                  Closed
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Clock size={10} className={clockColour} aria-hidden="true" />
                <span
                  className={`text-[10px] font-bold whitespace-nowrap ${closedTimeColour}`}
                >
                  {closureTimeDisplay}
                </span>
              </div>
            </>
          )}

          {isClosed && hasBooking && (
            <div className={`w-full h-px my-0.5 ${dividerColour}`} />
          )}

          <div className="flex items-center gap-2">
            {hasBooking && (
              <>
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-black relative flex-shrink-0"
                  style={{ backgroundColor: stageColour }}
                  aria-hidden="true"
                >
                  <div
                    className="absolute inset-0 rounded-full"
                    style={{ backgroundColor: "rgba(0, 0, 0, 0.25)" }}
                  />
                  <span
                    className="relative z-10 text-white font-black"
                    style={{ textShadow: "0 0 2px rgba(0,0,0,0.6)" }}
                  >
                    {stageInitial}
                  </span>
                </div>
                <span
                  className={`text-[11px] font-bold whitespace-nowrap ${timeColour}`}
                >
                  {bookingTimeRange}
                </span>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <User size={10} className={guestIconColour} />
                  <span className={`text-[10px] font-bold ${guestColour}`}>
                    {booking.guests}
                  </span>
                </div>
              </>
            )}
          </div>

          {nextBooking && (
            <>
              <div className={`w-full h-px my-0.5 ${dividerColour}`} />
              <div className="flex items-center gap-2">
                <ArrowRight
                  size={10}
                  className={themed("text-slate-300", "text-slate-600")}
                  aria-hidden="true"
                />
                <span
                  className={`text-[10px] whitespace-nowrap ${themed("text-slate-300", "text-slate-600")}`}
                >
                  {nextBookingTimeRange}
                </span>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <User
                    size={9}
                    className={themed("text-slate-300", "text-slate-600")}
                  />
                  <span
                    className={`text-[9px] ${themed("text-slate-300", "text-slate-600")}`}
                  >
                    {nextBooking.guests}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const FloatingBadge = memo(FloatingBadgeComponent);
